import React from 'react';

/**
 * Reveals an element once it scrolls into view.
 *
 * Deliberately CSS-driven: the hook only toggles a class, so the final visible
 * state is owned by the stylesheet. If frames are throttled (background tab,
 * heavy machine) or IntersectionObserver is unavailable, content still ends up
 * visible rather than stuck at opacity zero.
 */
export function useInView<T extends HTMLElement>(
  margin = '-80px'
): [React.RefObject<T | null>, boolean] {
  const ref = React.useRef<T | null>(null);
  const [inView, setInView] = React.useState(false);

  React.useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: margin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [margin]);

  return [ref, inView];
}
